import API, { post } from '../../api/baseUrl';
import { getTokens } from '../../auth/AuthState';
import {
  checkingClinicianProfileStatus,
  setClinicianProfile,
  setDashSettings,
} from './clinicianProfileBasicSlice';
import {
  checkingPatientProfileStatus,
  setPatientProfile,
  setPatientProfileState,
  setWellbeingPlan,
  setLoading,
} from './patientProfileSlice';
import {
  startLoadingInsightSettings,
  setInsightSettings,
  setMeds,
  endLoadingInsightSettings,
  startUpdatingInsightSettings,
  endUpdatingInsightSettings,
} from './insightSettingsSlice';
import { setPatientList, startLoadingPatientList } from './patientListSlice';
import {
  setPatientFeedbackList,
  startLoadingPatientFeedbackList,
} from './patientFeedbackListSlice';
import {
  chekingPatientFeedback,
  setPatientFeedback,
} from './patientFeedbackSlice';
import {
  setEventList,
  startLoadingEvents,
  endLoadingEvents,
} from './timelineSlice';
import {
  setPreEventList,
  startLoadingPreEvents,
  endLoadingPreEvents,
} from './predictionSlice';
import {
  setAllInsights,
  setLastUpdatedInsight,
  startLoadingInsights,
} from './insightsSlices';
import {
  setAllActions,
  setLastUpdatedAction,
  startLoadingActions,
} from './actionsSlices';
import { DataMapperTimeline } from '../../ai2/components/DataMapper/DataMapperTimeline';
import Swal from 'sweetalert2';
import { mapCaremapprUser } from './helpers';
import { toPatientProfileTO } from '../../lib';
import dispatchErrorEvent from '../../ai2/helpers/errorEvents';

export const err = (error) => {
  dispatchErrorEvent(error);
};

export const getClinicianProfileBasic = () => {
  return async (dispatch) => {
    dispatch(checkingClinicianProfileStatus());
    return await updateClinicProfile(dispatch);
  };
};

export const getClinicProfile = async () => {
  try {
    const clinicianProfileState = JSON.parse(
      localStorage.getItem('clinicianProfileState'),
    );
    const response = await post(`getClinicianProfileBasic`, {});
    const clinics = Array.isArray(response.data.data?.clinics)
      ? response.data.data?.clinics.map((clinic) => {
          return {
            ...clinic,
            primaryTeams: Array.isArray(clinic.primaryTeams)
              ? clinic.primaryTeams
              : [], // Ensure primaryTeams is an array
            healthProfessionals: Array.isArray(clinic.healthProfessionals)
              ? clinic.healthProfessionals
              : [], // Ensure healthProfessionals is an array
          };
        })
      : [];

    const teams = Array.isArray(response.data.data?.teams)
      ? response.data.data.teams
      : [];

    const allPrimaryTeams = clinics.flatMap((clinic) => clinic.primaryTeams); // Flatten the primaryTeams from all clinics
    const allHealthProfessionals = clinics.flatMap(
      (clinic) => clinic.healthProfessionals,
    ); // Flatten the healthProfessionals from all clinics
    const currentTenancy =
      response.data.data.tenancies.find(
        (tenancy) =>
          tenancy.tenancy_id === clinicianProfileState?.selectedTenancy,
      ) || response.data.data.tenancies[0];

    const allUsers = mapCaremapprUser(clinics, currentTenancy.tenancy_id);
    return {
      response,
      clinics,
      teams,
      allPrimaryTeams,
      allHealthProfessionals,
      allUsers,
      currentTenancy,
    };
  } catch (error) {
    if (error == 'No current user') {
      return;
    }
    console.error(error);
  }
};

export const updateClinicProfile = async (dispatch) => {
  const clinicProfile = await getClinicProfile();
  if (!clinicProfile) {
    return;
  }
  const {
    response,
    clinics,
    teams,
    allPrimaryTeams,
    allHealthProfessionals,
    allUsers,
    currentTenancy,
  } = clinicProfile;
  const data = {
    selectedTenancy: currentTenancy,
    firstName: response.data.data.firstName,
    lastName: response.data.data.lastName,
    details: response.data.data.details,
    accessToken: response.data.data.access_token,
    clinicianId: response.data.data.clinician_id,
    clinics: clinics,
    teams: teams,
    primaryTeams: allPrimaryTeams,
    healthProfessionals: allHealthProfessionals,
    tenancies: response.data.data.tenancies || [],
    message: response.data.message,
    dashSettings: response.data.data.dashSettings,
    allUsers: allUsers,
  };
  dispatch(setClinicianProfile(data));
  return data;
};

export const getPatientProfile = (
  access_token,
  selectedTenancy,
  silent = false,
) => {
  return async (dispatch) => {
    if (!selectedTenancy) return;
    if (!silent) {
      dispatch(checkingPatientProfileStatus());
    }

    try {
      if (!silent) {
        dispatch(setLoading(true));
      }
      const tokens = await getTokens();
      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      const body = {
        access_token: access_token,
        tenancyId: selectedTenancy?.tenancy_id,
      };

      const { data } = await post(`getPatientProfile`, body, options);
      if (!data.data) {
        throw new Error('No Access to patient profile');
      }

      if (data.data && data.data.length === 0) {
        throw new Error('No Access to patient profile');
      }
      const patientProfile = toPatientProfileTO(data.data[0]);
      dispatch(
        setPatientProfile({
          patientProfile,
          reconciliationSummary: data.reconciliationSummary,
          wellbeingPlan: data.wellbeingPlan,
          features: data.features,
          message: data.message,
          externalConnections: data.externalConnections || [],
        }),
      );
    } catch (error) {
      //Block of code to handle errors
      err(error);
    } finally {
      if (!silent) {
        dispatch(setLoading(false));
      }
    }
  };
};

export const updateWellbeingPlan = (
  patient_token,
  selectedTenancy,
  good,
  warning,
  wellBeing,
) => {
  return async (dispatch) => {
    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };
      post(
        `updateWellbeingPlan`,
        {
          access_token: patient_token,
          tenancyId: selectedTenancy?.tenancy_id,
          good: good,
          warning: warning,
        },
        options,
      );
      const temp = Object.assign({}, wellBeing);
      temp.user_sig_sign_options = [
        { good_signs: good, warning_signs: warning },
      ];
      dispatch(setWellbeingPlan({ wellbeingPlan: temp }));
    } catch (error) {
      //Block of code to handle errors
      console.error(error);
    }
  };
};

export const updatePatientProfile = (rowData) => {
  return async (dispatch, getState) => {
    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };
      const { data } = await post(`updatePatientProfile`, rowData, options);
      const patientProfile = toPatientProfileTO(data.data.data[0]);

      if (data && data.data.message === 'Success') {
        dispatch(checkingPatientProfileStatus());
        dispatch(
          setPatientProfileState({
            patientProfile,
          }),
        );
      }
    } catch (error) {
      //Block of code to handle errors
      console.error(error);
      try {
        const state = typeof getState === 'function' ? getState() : null;
        const selectedTenancy = state?.clinicianProfile?.selectedTenancy;
        if (rowData?.access_token && selectedTenancy) {
          dispatch(
            getPatientProfile(rowData.access_token, selectedTenancy, true),
          );
        }
      } catch (e) {
        console.error(e);
      }
    }
  };
};

export const postRequestSync = async (accessToken, selectedTenancy) => {
  try {
    const tokens = await getTokens();

    let options = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + tokens.id,
      },
    };
    await post(
      `requestSync`,
      { access_token: accessToken, tenancyId: selectedTenancy?.tenancy_id },
      options,
    );
  } catch (error) {
    //Block of code to handle errors
    console.error(error);
  }
};

export const getPatientList = (page = 1, sort, filter, selectedTenancy) => {
  return async (dispatch) => {
    dispatch(startLoadingPatientList());

    try {
      if (!selectedTenancy) {
        return;
      }
      const tokens = await getTokens();
      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      const body = {
        tenancyId: selectedTenancy?.tenancy_id, //tenancyId which you are searching in
        clinics: filter.clinics, //.split(',').map(Number), // array of clinic id ints
        primaryTeams: filter.primaryTeams, // array of search strings
        healthProfessionals: filter.healthProfessionals, // array of search strings
        episodeStatus: filter.episodeStatus, //open or closed
        insightCategory: filter.insightCategory, // array of search chars (h, m, l?)
        tags: filter.tags, // arrray of search strings
        patientName: filter.patientName, //free test search against firstname and last name
        event_subcategory_id: filter.event_subcategory_id,
        event_subcategory_name: filter.event_subcategory_name,
        assigned: filter.assigned, //
        favourite: filter.favourite, //
        page: page, //page num
        max: 10, //amount per page
        sort: sort.sort, // first_name or last_name
        direction: sort.direction, //ASC or DESC
      };

      const { data } = await post(`getPatientList`, body, options);

      if (data.message == 'Error') {
        throw new Error('Error');
      }

      dispatch(
        setPatientList({
          patients: data.data,
          page: page,
          totalSize: data.config.totalSize,
          currentPage: data.config.currentPage,
          currentMax: data.config.currentMax,
        }),
      );
    } catch (error) {
      //Block of code to handle errors
      err(error);
    }
  };
};

export const getPatientFeedbackList = (
  user,
  selectedTenancy,
  nav,
  stateSetters,
) => {
  return async (dispatch) => {
    if (!selectedTenancy) {
      return;
    }
    dispatch(startLoadingPatientFeedbackList());
    try {
      const tokens = await getTokens();
      const { data } = await post(
        `getPatientFeedbackList`,
        {
          tenancyId: selectedTenancy.tenancy_id,
          access_token: user.access_token,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + tokens.id,
          },
        },
      );

      // Always stop loading first
      if (stateSetters?.setIsLoadingFeedback) {
        stateSetters.setIsLoadingFeedback(false);
      }

      // Only navigate directly if explicitly marked as completed
      if (data.message === 'AlreadyCompleted') {
        nav(`/patientDetails?access_token=${user.access_token}`, {});
        return;
      }

      // Show popup if message is Success
      if (data.message === 'Success' && data.data) {
        if (stateSetters && typeof stateSetters.setPatient === 'function') {
          stateSetters.setPatient({
            ...user,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            access_token: user.access_token,
            id: user.id,
          });
          stateSetters.setShowViewPatient(true);
        }

        dispatch(
          setPatientFeedbackList({
            isLoading: false,
            id: data.data[0]?.id,
            questions: data.data[0]?.questions,
            message: data.message,
          }),
        );
      } else if (
        data.message !== 'Success' &&
        data.message !== 'AlreadyCompleted'
      ) {
        nav(`/patientDetails?access_token=${user.access_token}`, {});
      }
    } catch (error) {
      console.error('Error fetching patient feedback:', error);
      // Make sure to stop loading on error
      if (stateSetters?.setIsLoadingFeedback) {
        stateSetters.setIsLoadingFeedback(false);
      }
      nav(`/patientDetails?access_token=${user.access_token}`, {});
    }
  };
};

export const addPatientFeedback = (
  patient,
  userQuestionnaireId,
  questionMap,
  status,
  selectedTenancy,
) => {
  return async (dispatch) => {
    dispatch(chekingPatientFeedback());

    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      const body = {
        userQuestionnaireId: userQuestionnaireId,
        access_token: patient.access_token,
        tenancyId: selectedTenancy?.tenancy_id,
        status: status,
        questionsMap: questionMap,
      };

      const { data } = await post(`addPatientFeedback`, body, options);

      const audit = {
        access_token: patient.access_token,
        interactionType: questionMap[1].answerText,
        tenancyId: selectedTenancy?.tenancy_id,
      };

      const { pc } = await post(`createProfileCheck`, audit, options);

      dispatch(setPatientFeedback({ message: data.message, data: data.data }));
    } catch (error) {
      //Block of code to handle errors
      console.error(error);
    }
  };
};

export const getTimeline = (
  access_token,
  startDate,
  endDate,
  selectedTenancy,
  silent = false,
) => {
  return async (dispatch) => {
    if (!silent) {
      dispatch(startLoadingEvents());
    }

    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      const body = {
        access_token: access_token,
        tenancyId: selectedTenancy?.tenancy_id,
        startDate: startDate,
        endDate: endDate,
      };

      const { data } = await post(`getTimeline`, body, options);
      const tenancyConfig = selectedTenancy?.features?.timelineConfig || '';
      const isAutomedic = tenancyConfig?.timeline === 'automedicPayload';
      if (data.data && data.data.events) {
        const mappedEvents = DataMapperTimeline(
          data.data.events,
          data.data.insights,
          data.data.actions || null,
          data.data.profileChecks,
          startDate,
          endDate,
          isAutomedic,
        );

        dispatch(
          setEventList({
            groups: mappedEvents.groups,
            items: mappedEvents.items,
            startDate: startDate,
            endDate: endDate,
            observationTable: data?.data?.observationTable || {},
            rawEvents: data?.data?.events || [],
            latestEpisodeId: data?.data?.latestEpisodeId || null,
          }),
        );
      }
    } catch (error) {
      //Block of code to handle errors
      err(error);
    } finally {
      dispatch(endLoadingEvents());
    }
  };
};

export const getPreTimeline = (
  access_token,
  startDate,
  endDate,
  selectedTenancy,
) => {
  return async (dispatch) => {
    if (!selectedTenancy) return;
    dispatch(startLoadingPreEvents());

    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      const body = {
        access_token: access_token,
        tenancyId: selectedTenancy?.tenancy_id,
        startDate: startDate,
        endDate: endDate,
      };

      const { data } = await post(`getTimeline`, body, options);
      if (data.data && data.data.events) {
        const mappedEvents = DataMapperTimeline(
          data.data.events,
          data.data.insights,
          data.data.actions || null,
          data.data.profileChecks,
          startDate,
          endDate,
        );

        dispatch(
          setPreEventList({
            groups: mappedEvents.groups,
            items: mappedEvents.items,
            startDate: startDate,
            endDate: endDate,
          }),
        );
      }
    } catch (error) {
      //Block of code to handle errors
      err(error);
    } finally {
      dispatch(endLoadingPreEvents());
    }
  };
};

export const getAllInsights = (access_token, selectedTenancy) => {
  return async (dispatch) => {
    if (!selectedTenancy) return;
    dispatch(startLoadingInsights());

    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      const body = {
        access_token: access_token,
        tenancyId: selectedTenancy?.tenancy_id,
      };

      const { data } = await post(`getAllInsights`, body, options);

      dispatch(setAllInsights({ message: data.message, insights: data.data }));
    } catch (error) {
      //Block of code to handle errors
      console.error(error);
    }
  };
};

export const getAllActions = (access_token, selectedTenancy) => {
  return async (dispatch) => {
    if (!selectedTenancy) return;
    dispatch(startLoadingActions());

    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      const body = {
        access_token: access_token,
        tenancyId: selectedTenancy?.tenancy_id,
      };

      const { data } = await post(`getAllActions`, body, options);

      if (data.message != 'Error') {
        dispatch(setAllActions({ message: data.message, actions: data.data }));
      }

      return data.data;
    } catch (error) {
      //Block of code to handle errors
      console.error(error);
      return error;
    }
  };
};

/**
 *
 * @param {ActionInsightRequest} insightAction
 * @param {*} selectedTenancy
 * @returns
 */
export const addInsightAction = (insightAction, selectedTenancy) => {
  return async (dispatch) => {
    try {
      insightAction.tenancyId = selectedTenancy?.tenancy_id;

      const { data } = await post(`actionInsight`, insightAction);

      dispatch(
        setLastUpdatedInsight({
          updatedInsight: insightAction.insightId,
          actionId: data.data?.[0].id,
        }),
      );
      dispatch(
        getPatientProfile(insightAction.access_token, selectedTenancy, true),
      );
    } catch (error) {
      //Block of code to handle errors
      dispatch(setLastUpdatedInsight({ updatedInsight: null, actionId: null }));
      console.error(error);
    }
  };
};

export const addAction = (action, selectedTenancy) => {
  return async (dispatch) => {
    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      action.tenancyId = selectedTenancy.tenancy_id;

      const { data } = await post(`action`, action, options);

      dispatch(
        setLastUpdatedAction({
          updatedAction: action.actionId,
          actionId: data.data?.[0].id,
        }),
      );
      return data.data?.[0].id;
    } catch (error) {
      //Block of code to handle errors
      dispatch(setLastUpdatedAction({ updatedAction: null, actionId: null }));
      console.error(error);
      return error;
    }
  };
};

export const getInsightSettings = (
  access_token,
  tenancyId,
  silence = false,
) => {
  return async (dispatch) => {
    if (!silence) {
      dispatch(startLoadingInsightSettings());
    }

    try {
      const { data } = await post(`getInsightSettings`, {
        access_token,
        tenancyId,
      });
      dispatch(
        setInsightSettings({
          message: data.message,
          insightSettings: data.data,
        }),
      );
    } catch (error) {
      //Block of code to handle errors
      err(error);
    } finally {
      dispatch(endLoadingInsightSettings());
    }
  };
};

export const getAmtItems = (searchText) => {
  return async (dispatch) => {
    try {
      const tokens = await getTokens();
      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      post(`getAmtItems`, { searchText: searchText }, options)
        .then((res) => {
          if (res.data.data && res.data.data.length > 0) {
            dispatch(setMeds({ meds: res.data.data }));
          }
        })
        .catch((err) => {
          throw err;
        });
    } catch (error) {
      //Block of code to handle errors
      console.error(error);
    }
  };
};

export const updateInsightSettings = (...args) => {
  return async (dispatch) => {
    dispatch(startUpdatingInsightSettings());
    try {
      let payload;
      if (
        args.length === 1 &&
        typeof args[0] === 'object' &&
        args[0] !== null
      ) {
        // New signature: single payload object
        payload = args[0];
      } else {
        // Backward-compatible signature
        const [
          selectedTenancy,
          access_token,
          medication_settings,
          appointment_settings,
        ] = args;
        payload = {
          tenancyId: selectedTenancy?.tenancy_id,
          access_token,
          medication_settings,
          appointment_settings,
        };
      }

      await post(`updateInsightSettings`, payload);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(endUpdatingInsightSettings());
    }
  };
};

export const invitePatient = (
  access_token,
  selectedTenancy,
  phone,
  clinicName,
) => {
  return async (dispatch) => {
    try {
      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      const body = {
        access_token: access_token,
        tenancyId: selectedTenancy?.tenancy_id,
        phone: phone,
        clinic: clinicName,
      };

      const { data } = await post(`invitePatient`, body, options);

      await Swal.fire({
        title: 'Success!',
        text: 'Patient has been invited to CareMappr',
        icon: 'success',
        button: 'Close',
      });

      //get latest patientProfile as the active flag has changed.
      dispatch(getPatientProfile(access_token, selectedTenancy));
    } catch (error) {
      //Block of code to handle errors
      err(error);
    }
  };
};

export const updateClinicianProfile = (dash_settings) => {
  return async (dispatch) => {
    try {
      dispatch(setDashSettings(dash_settings));

      const tokens = await getTokens();

      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };

      // in future we can expand on this payload to include other profile things
      const { data } = await post(
        `updateClinicianProfile`,
        { dashSettings: dash_settings },
        options,
      );
    } catch (error) {
      console.error(error);
    }
  };
};

// export const startCreatingUserWithEmailpassword = ({ email, password, displayName }) => {
//     return async(dispatch) => {

//         dispatch( chekingCredentials() );

//         const { ok, uid, photoURL, errorMessage } = await registerUserWithEmailPassword({ email, password, displayName });

//         if ( !ok ) return dispatch( logout({errorMessage}) );

//         dispatch ( login( { uid, displayName, email, photoURL } ) );

//     }

// }
